import {Input} from '@mantine/core';
import {Text} from 'src/design/atoms/Text';
import type {Delta as DeltaType} from 'quill';
import ImageResize from 'quill-image-resize-module-react';
import React, {useCallback, useEffect, useMemo, useRef, useState} from 'react';
import ReactQuill, {Quill} from 'react-quill';
import 'react-quill/dist/quill.snow.css';
import {useAppSelector} from 'src/redux/hooks';
import {useAlert} from 'src/shared/hooks/useAlert';
import {
  fixRichTextTokens,
  imageFileFromUrl,
  uploadImage,
} from 'src/shared/utils/imageHandler';
import {parseErrorMessage} from 'src/shared/utils/parseErrorMessage';
import type {IFormFieldV2} from 'src/types/FormV2';
import {v4 as uuidv4} from 'uuid';
import {FormViewRichTextEditor} from '../FormViewRichTextEditor/FormViewRichTextEditor';
import './FormRichTextEditorV2.css';

Quill.register('modules/imageResize', ImageResize);
const Delta = Quill.import('delta') as typeof DeltaType;

export interface FormRichTextEditorV2Props {
  field: IFormFieldV2;
  value: string;
  error?: string;
  onChange: (name: string, value: string) => void;
  disableExternalContent?: boolean;
  readOnly?: boolean;
}

export const FormRichTextEditorV2 = React.memo<FormRichTextEditorV2Props>(
  ({
    field,
    value,
    error,
    onChange,
    disableExternalContent = false,
    readOnly = false,
  }) => {
    const {accessToken} = useAppSelector(state => state.auth);
    const {setAlert} = useAlert();
    const [id] = useState(uuidv4());
    const quillRef = useRef<ReactQuill>(null);

    useEffect(() => {
      const QuillLink = Quill.import('formats/link');
      QuillLink.sanitize = (url: string) => {
        // quill by default creates relative links if scheme is missing.
        if (!url.startsWith('http://') && !url.startsWith('https://')) {
          return `http://${url}`;
        }
        return url;
      };
      Quill.register(QuillLink, true);
    }, []);

    function imageHandler() {
      const input = document.createElement('input');

      input.setAttribute('type', 'file');
      input.setAttribute('accept', 'image/*');
      input.click();
      input.onchange = async () => {
        try {
          const file: any = input && input.files ? input.files[0] : null;

          const url = await uploadImage(file, accessToken);

          const quill = quillRef?.current?.getEditor();
          const range = quill?.getSelection();

          quill?.insertEmbed(range?.index as number, 'image', url);
          quill?.setSelection(range!.index + 10, 0);
        } catch (error: any) {
          setAlert(parseErrorMessage(error), 'error');
        }
      };
    }

    const imagePaster = (node: HTMLImageElement, delta: any) => {
      if (node.src.indexOf('?jwt=') === -1) {
        imageFileFromUrl(node)
          .then((response: File) => {
            return uploadImage(response, accessToken).then(url => {
              const quill = quillRef?.current?.getEditor();
              const range = quill?.getSelection();

              quill?.insertEmbed(range?.index as number, 'image', url);
              quill?.setSelection(range!.index + 10, 0);

              return new Delta().retain(range!.index);
            });
          })
          .catch(error => {
            setAlert(parseErrorMessage(error), 'error');
          });
      } else {
        return new Delta(delta.ops);
      }

      return new Delta().retain(0);
    };

    const defaultContainerModules = [
      [{size: ['small', false, 'large', 'huge']}],
      ['bold', 'italic', 'underline'],
      [{list: 'ordered'}, {list: 'bullet'}],
      [{color: []}, {background: []}],
      [{indent: '-1'}, {indent: '+1'}],
    ];

    const defaultQuillModules = {
      toolbar: {
        container: defaultContainerModules,
      },
    };

    const quillModulesWithExternalContent = {
      toolbar: {
        container: [...defaultContainerModules, ['image'], ['video'], ['link']],
        handlers: {
          image: imageHandler,
        },
      },
      imageResize: {
        parchment: Quill.import('parchment'),
        modules: ['Resize', 'DisplaySize'],
      },
      clipboard: {
        matchers: [['img', imagePaster]],
      },
    };

    const quillModules = useMemo(
      () =>
        disableExternalContent === true
          ? defaultQuillModules
          : quillModulesWithExternalContent,
      [disableExternalContent]
    );

    const handleOnChange = useCallback(
      (content: string): void => {
        onChange(field.name, content || '');
      },
      [field.name, onChange]
    );

    const maxLength = field.validation?.maxLength;
    const currentLength = (value || '').length;

    const calcValue = fixRichTextTokens(value || '', accessToken);

    if (readOnly) {
      return (
        <FormViewRichTextEditor
          label={field.label}
          value={calcValue}
          description={field.description}
        />
      );
    }

    return (
      <Input.Wrapper
        data-testid={`form-field-${field.name}`}
        label={field.label}
        description={field.description}
        error={error}
        withAsterisk={field.required}>
        <div
          className={`quill-editor-wrapper${
            error ? ' quill-editor-error' : ''
          }`}
          data-text-editor={`${id}`}
          style={{paddingTop: '1rem'}}>
          <ReactQuill
            ref={quillRef}
            modules={quillModules}
            readOnly={field.disabled}
            onChange={handleOnChange}
            value={calcValue}
            placeholder="Content goes here..."
            theme="snow"
            scrollingContainer={'body'}
            bounds={`[data-text-editor="${id}"]`}
          />
        </div>
        {maxLength && (
          <Text
            size="xs"
            c={currentLength > maxLength ? 'red' : 'dimmed'}
            ta="right"
            mt={4}>
            {currentLength.toLocaleString()} / {maxLength.toLocaleString()}
          </Text>
        )}
      </Input.Wrapper>
    );
  }
);
